import type { Quality, Version } from '../types.ts';
import type { SyncClient } from '../hooks/useSyncClient.ts';
import type { ClipGroup } from '../hooks/useClipGroups.ts';

interface Props {
  playlistName: string;
  group: ClipGroup | null;
  version: Version | null;
  onChooseVersion: (shotId: string, versionId: string) => void;
  quality: Quality;
  onQuality: (quality: Quality) => void;
  sync: SyncClient;
  soloMode: boolean;
  onToggleSolo: () => void;
  /** Only a token holder may take the room over; guests never see the button. */
  canTakeControl: boolean;
}

/** Title bar: which clip is under review on the left, who else is in the room on the right. */
export function ReviewHeader({
  playlistName,
  group,
  version,
  onChooseVersion,
  quality,
  onQuality,
  sync,
  soloMode,
  onToggleSolo,
  canTakeControl,
}: Props): React.JSX.Element {
  const others = sync.participants.filter((p) => p.clientId !== sync.clientId);
  // Being presented to: the room is locked and someone else holds it.
  const presentedTo = sync.locked && !sync.isController;

  return (
    <header className="review__bar">
      <div className="review__title">
        {version ? (
          <a className="review__home" href={`/?project=${version.projectId}`} title="Back to project">
            {playlistName}
          </a>
        ) : (
          <strong>{playlistName}</strong>
        )}
        {group ? (
          <span className="review__clip">
            {group.shotName}
            {group.versions.length > 1 ? (
              <select
                className="review__version"
                value={version?.id ?? ''}
                onChange={(e) => onChooseVersion(group.shotId, e.target.value)}
                title="Version"
              >
                {/* Newest first: that is the one you reach for. */}
                {[...group.versions].reverse().map((it) => (
                  <option key={it.version.id} value={it.version.id}>
                    {it.version.name}
                  </option>
                ))}
              </select>
            ) : (
              <span className="muted"> {version?.name}</span>
            )}
            {/* Only offered when the native rendition is above 1080p — a 1080p clip has no cut
                to switch to, and the dropdown was pure noise on those. */}
            {(version?.media.fullHeight ?? 0) > 1080 ? (
              <select
                className="review__quality"
                value={quality}
                onChange={(e) => onQuality(e.target.value as Quality)}
                title="Streaming quality"
              >
                <option value="full">4K</option>
                <option value="proxy" disabled={!version?.media.proxy}>
                  HD
                </option>
              </select>
            ) : null}
          </span>
        ) : (
          <span className="muted">—</span>
        )}
      </div>

      <div className="review__participants">
        <span className={`dot${sync.connected ? ' dot--on' : ''}`} title="Sync connection" />
        <span className="muted">{sync.participants.length} in room</span>
        {others.map((p) => (
          <span key={p.clientId} className={`chip${p.isController ? ' chip--controller' : ''}`}>
            {p.name}
            {p.isController ? ' 🎬' : ''}
          </span>
        ))}
        {/* Only a token holder gets the remote. The label follows the lock, not mere
            controllership: a host who is simply first into an unlocked room still reads "Take
            control", and only flips to "Release control" once they have actually locked it. */}
        {canTakeControl ? (
          sync.locked && sync.isController ? (
            <button type="button" onClick={sync.releaseControl}>
              Release control
            </button>
          ) : (
            <button type="button" className="primary" onClick={sync.takeControl} disabled={soloMode}>
              Take control
            </button>
          )
        ) : null}
        {/* Leave sync lets a reviewer jump ahead a few frames while the room watches a slow crawl;
            comments and status still stream, only play/seek/version-jump stop mirroring. It is
            gone while the host is presenting — then everyone stays on the host's frame. */}
        {presentedTo ? (
          <span className="chip chip--locked" title="The host has taken control of this review">
            🔒 presenting
          </span>
        ) : (
          <button type="button" className={soloMode ? 'active' : ''} onClick={onToggleSolo}>
            {soloMode ? 'Rejoin sync' : 'Leave sync'}
          </button>
        )}
      </div>
    </header>
  );
}
